import { useEffect, useState } from 'react'
import { Container, Row, Col, Form, Button, Modal } from 'react-bootstrap';
import DynamicForm from './DynamicForm';
import { useNavigate, Link, useParams } from 'react-router-dom';
import { retryAsyncs } from '../../utils/retryAsync';
import { getAccountsByCif, getManagerByTransaction } from '../../services';
import { saveCostStep, getCostStep } from "../../services/costWizardService";
import { getParametersConfig } from '../../services/parametersService';
import { properties } from '../../utils/Properties_es';
import { useCatalogs } from '../../hooks/useCatalogs';
import { regexPatterns } from '../utils/regexUtils';

export default function CostDataForm({ onValidate, data, updateData }) {

  const { id: idTransaction } = useParams();
  const navigate = useNavigate();
  const [errorMsg, setErrorMsg] = useState('');
  const [accounts, setAccounts] = useState([]);
  const [costConfig, setCostConfig] = useState(null);
  const [loadingCosts, setLoadingCosts] = useState(true);
  const [showExtraDataModal, setShowExtraDataModal] = useState(false);
  const [modalErrors, setModalErrors] = useState({});
  const { catalogs } = useCatalogs();


  // 🔹 Cuentas
  useEffect(() => {
    async function loadAccounts() {
      try {
        const managerPage = await retryAsyncs(() => getManagerByTransaction(idTransaction), 3, 1500);
        const cifBco = managerPage?.cifBco;

        if (!cifBco) {
          console.error("No se encontró cifBco en ManagerPage");
          return;
        }

        const dataResponse = await retryAsyncs(() => getAccountsByCif(cifBco), 3, 1500);
        setAccounts(dataResponse?.accountInformation ?? []);
      } catch (error) {
        console.error("error cargando cuentas", error)
      }
    }
    loadAccounts();
  }, [idTransaction]);

  // 🔹 Costos desde backend
  useEffect(() => {
    async function loadCostConfig() {
      try {
        const response = await getParametersConfig();
        if (Array.isArray(response) && response.length > 0) {
          setCostConfig(response[0]);
        }
      } catch (error) {
        console.error("Error cargando configuración de costos", error);
      } finally {
        setLoadingCosts(false);
      }
    }

    loadCostConfig();
  }, []);

  const cuentas = {
    colones: accounts.filter(v => v.accountCurrency === 'CRC').map(v => ({ key: v.accountNumber, value: v.accountNumber })),
    dolares: accounts.filter(v => v.accountCurrency === 'USD').map(v => ({ key: v.accountNumber, value: v.accountNumber })),
  };

  const sanitizeAmountInput = (value) =>
    String(value ?? '')
      .split('')
      .filter((char) => regexPatterns.onlyNumbers.test(char))
      .join('');

  const sanitizeReasonInput = (value) =>
    String(value ?? '')
      .toUpperCase()
      .split('')
      .filter((char) => regexPatterns.noSpecialChars.test(char.toLowerCase()) || char === ' ')
      .join('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setErrorMsg('');

    if (name === 'moneda') {
      updateData({
        moneda: value,
        cuentaSeleccionadaCRC: '',
        cuentaSeleccionadaUSD: '',
        montoTotalDepositoCRC: '',
        montoTotalDepositoUSD: '',
      })
    } else if (name === 'recibeTransferenciasInternacionales') {
      updateData({ [name]: value });
      if (value === 'si') {
        setShowExtraDataModal(true);
      } else {
        updateData({
          tiMontoEntranteUsd: '',
          tiMontoSalienteUsd: '',
          tiMotivoUsoRecurrente: '',
        });
        setModalErrors({});
      }
    } else if (name === 'tiMotivoUsoRecurrente') {
      updateData({ [name]: sanitizeReasonInput(value) });
    } else if (
      name === 'montoTotalDepositoUSD' ||
      name === 'montoTotalDepositoCRC' ||
      name === 'tiMontoEntranteUsd' ||
      name === 'tiMontoSalienteUsd'
    ) {
      updateData({ [name]: sanitizeAmountInput(value) });
    } else {
      updateData({ [name]: value });
    }
  };

  const handleModalCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setErrorMsg('');
    updateData({ [name]: checked });
  };

  const getModalValidationErrors = () => {
    const errors = {};

    if (data.recibeTransferenciasInternacionales === 'si') {
      if (!data.tiMontoEntranteUsd?.trim()) {
        errors.tiMontoEntranteUsd = properties.cost_ti_error_amount_in;
      }
      if (!data.tiMontoSalienteUsd?.trim()) {
        errors.tiMontoSalienteUsd = properties.cost_ti_error_amount_out;
      }
      if (!data.tiMotivoUsoRecurrente?.trim()) {
        errors.tiMotivoUsoRecurrente = properties.cost_ti_error_reason;
      }
    }

    return errors;
  };

  const validateModalData = ({ setUiErrors = true } = {}) => {
    const errors = getModalValidationErrors();
    if (setUiErrors) {
      setModalErrors(errors);
    }
    return Object.keys(errors).length === 0;
  };

  const handleSaveModal = () => {
    if (!validateModalData({ setUiErrors: true })) {
      return;
    }
    setShowExtraDataModal(false);
  };

  const isFormValid = () => {
    if (data.moneda === 'AMBO' && (!data.cuentaSeleccionadaCRC || !data.cuentaSeleccionadaUSD)) return false;
    if (data.moneda === 'CRC' && !data.cuentaSeleccionadaCRC) return false;
    if (data.moneda === 'USD' && !data.cuentaSeleccionadaUSD) return false;

    if (data.moneda === 'USD' && !data.montoTotalDepositoUSD?.trim()) return false;
    if (data.moneda === 'CRC' && !data.montoTotalDepositoCRC?.trim()) return false;
    if (data.moneda === 'AMBO' && (!data.montoTotalDepositoCRC?.trim() || !data.montoTotalDepositoUSD?.trim())) return false;

    if (!data.industriaSeleccionada) return false;
    if (!data.recibeTransferenciasInternacionales) return false;

    if (Object.keys(getModalValidationErrors()).length > 0) return false;

    return true;
  };

  const fieldConfig = [
    [{
      key: 'moneda', label: properties.cost_currency_question, type: 'radio',
      options: [{ key: properties.cost_option_colones, value: 'CRC' }, { key: properties.cost_option_dollars, value: 'USD' }, { key: properties.cost_option_both, value: 'AMBO' }]
    }],
    [{
      key: 'cuentaSeleccionadaCRC', label: properties.cost_account_question, type: 'select',
      options: cuentas.colones, disabled: !(data.moneda === 'CRC' || data.moneda === 'AMBO')
    }],
    [{
      key: 'cuentaSeleccionadaUSD', label: properties.cost_account_question, type: 'select',
      options: cuentas.dolares, disabled: !(data.moneda === 'USD' || data.moneda === 'AMBO')
    }],
    ...(data.moneda === 'USD' || data.moneda === 'AMBO'
      ? [[{
        key: 'montoTotalDepositoUSD', label: properties.cost_expected_amount_usd, type: 'text',
        isRequired: true,
        regex: regexPatterns.onlyNumbers,
        maxCaracters: 25,
      }]]
      : []),
    ...(data.moneda === 'CRC' || data.moneda === 'AMBO'
      ? [[{
        key: 'montoTotalDepositoCRC', label: properties.cost_expected_amount_crc, type: 'text',
        isRequired: true,
        regex: regexPatterns.onlyNumbers,
        maxCaracters: 25,
      }]]
      : []),
    [{
      key: 'industriaSeleccionada', label: properties.cost_industry_question, type: 'select', isRequired: true,
      options: [
          ...(catalogs.industryTypes?.map(i => ({ key: i.industryType, value: String(i.id).trim() })) || [])
      ]
    }],
    [{
      key: 'recibeTransferenciasInternacionales', label: properties.cost_international_question, type: 'radio', isRequired: true,
      options: [{ key: properties.general_yes, value: 'si' }, { key: properties.general_no, value: 'no' }]
    }],
  ];

  const handleContinue = async (e) => {
    e.preventDefault();
    if (!isFormValid()) {
      validateModalData({ setUiErrors: true });
      setErrorMsg(properties.cost_error_selection_extended);
      if (data.recibeTransferenciasInternacionales === 'si') {
        setShowExtraDataModal(true);
      }
      return;
    }

    try {
      await saveCostStep({ idTransaction, data });
      onValidate(true);
      navigate(`/forms/${idTransaction}/cicac`);
    } catch (error) {
      console.error("Error al enviar:", error);
      //alert(properties.cost_error_save);
    }
  };

  // 🔹 Carga previa de costos
  useEffect(() => {
    async function loadInitialData() {
      try {
        const cost = await getCostStep(idTransaction);
        if (!cost) return;
        const hasTiData = Boolean(
          cost.incomingAmount || cost.outgoingAmount || cost.detail
        );
        updateData({
          moneda: cost.membershipCurrency ?? "",
          cuentaSeleccionadaCRC: cost.crcAccount ?? "",
          cuentaSeleccionadaUSD: cost.usdAccount ?? "",
          montoTotalDepositoCRC: cost.totalDepositAmountCrc ?? "",
          montoTotalDepositoUSD: cost.totalDepositAmountUsd ?? "",
          industriaSeleccionada:
            cost.apnfd === false
              ? "0"
              : cost.industryId
                ? String(cost.industryId).trim()
                : "",
          recibeTransferenciasInternacionales: hasTiData ? "si" : "no",
          tiMontoEntranteUsd: cost.incomingAmount ?? "",
          tiMontoSalienteUsd: cost.outgoingAmount ?? "",
          tiMotivoUsoRecurrente: cost.detail ?? "",
        });
      } catch {
        console.log("No hay costos previos");
      }
    }
    loadInitialData();
  }, []);

  if (loadingCosts) {
    return (
      <Container className="py-5 text-center">
        <strong>{properties.cost_loading}</strong>
      </Container>
    );
  }

  return (
    <Container className="py-4 px-4">
      <h5 className="mb-4"><strong>{properties.cost_form_title}</strong></h5>

      <Row className="g-4 align-items-start">
        <Col lg={7}>
          <div className="border rounded p-3 p-md-4">
            <Form onSubmit={handleContinue}>
            <h6 className="fw-bold mb-3">{properties.cost_expected_transactions_title}</h6>
            <DynamicForm fieldConfig={fieldConfig} formData={data} handleChange={handleChange} notClass={true} />

            {data.recibeTransferenciasInternacionales === 'si' && (
              <div className="mt-2">
                <Button
                  variant="outline-primary"
                  className="rounded-0"
                  onClick={() => setShowExtraDataModal(true)}
                >
                  {properties.cost_ti_section_title}
                </Button>
              </div>
            )}

            {errorMsg && <div className="text-danger small mt-2">{errorMsg}</div>}
            </Form>
          </div>
        </Col>

        <Col lg={5} className="mb-4 mb-lg-0">
          <div className="border rounded p-3 p-md-4 h-100">

          <Row className="mb-2">
            <Col><div className="d-flex justify-content-between">
              <span>{properties.costs_annual_membership}</span>
              <strong>${costConfig?.annualMembership ?? "-"}</strong>
            </div></Col>
          </Row>

          <hr />

          <Row className="mb-2">
            <Col>
              <span>{properties.costs_central_bank_commission}</span>
              <ul className="list-unstyled mt-2 mb-2 ps-0">
                <li className="d-flex flex-column flex-sm-row align-items-start align-items-sm-center justify-content-sm-between gap-1 gap-sm-3 mb-2">
                  <span>{properties.costs_local}:</span>
                  <strong className="text-start d-block">{costConfig?.localCommissionCR ?? "-"}{properties.costs_per_transaction}</strong>
                </li>
                <li className="d-flex flex-column flex-sm-row align-items-start align-items-sm-center justify-content-sm-between gap-1 gap-sm-3">
                  <span>{properties.costs_international}:</span>
                  <strong className="text-start d-block">{costConfig?.internationalCommissionCr ?? "-"}{properties.costs_per_transaction}</strong>
                </li>
              </ul>
            </Col>
          </Row>

          <hr />

          <Row className="mb-2">
            <Col><div className="d-flex justify-content-between">
              <span>{properties.costs_dataphone_rental}</span>
              <strong>₡{costConfig?.dataphoneRental?.toLocaleString() ?? "-"}</strong>
            </div></Col>
          </Row>

          <hr />

          <Row>
            <Col><div className="d-flex justify-content-between">
              <span>{properties.costs_maintenance_account}</span>
              <strong>${costConfig?.maintenance ?? "-"}</strong>
            </div></Col>
          </Row>
          </div>
        </Col>
      </Row>

      <Row className="mt-5">
        <Col className="d-flex justify-content-end gap-2">
          <Button variant="outline-primary" className="px-5 py-2 shadow rounded-0" as={Link} to={`/forms/${idTransaction}/files`}>{properties.btn_back}</Button>
          <Button className="px-5 py-2 shadow rounded-0" variant="primary" disabled={!isFormValid()} onClick={handleContinue}>{properties.btn_continue}</Button>
        </Col>
      </Row>

      <Modal
        show={showExtraDataModal}
        onHide={() => setShowExtraDataModal(false)}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>{properties.cost_ti_section_title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {data.recibeTransferenciasInternacionales === 'si' && (
            <div className="border border-primary rounded">
              <div className="px-3 py-2 text-white bg-primary">
                <strong>{properties.cost_ti_section_title}</strong>
              </div>
              <div className="p-3">
                <Form.Group className="mb-3">
                  <Form.Label className="fw-bold text-success">{properties.cost_ti_amount_in}</Form.Label>
                  <Form.Control
                    type="text"
                    name="tiMontoEntranteUsd"
                    value={data.tiMontoEntranteUsd ?? ''}
                    onChange={handleChange}
                    inputMode="numeric"
                    pattern="[0-9]*"
                    className="rounded-0 border-success"
                    maxLength={25}
                  />
                  {modalErrors.tiMontoEntranteUsd && <div className="text-danger small mt-1">{modalErrors.tiMontoEntranteUsd}</div>}
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label className="fw-bold text-success">{properties.cost_ti_amount_out}</Form.Label>
                  <Form.Control
                    type="text"
                    name="tiMontoSalienteUsd"
                    value={data.tiMontoSalienteUsd ?? ''}
                    onChange={handleChange}
                    inputMode="numeric"
                    pattern="[0-9]*"
                    className="rounded-0 border-success"
                    maxLength={25}
                  />
                  {modalErrors.tiMontoSalienteUsd && <div className="text-danger small mt-1">{modalErrors.tiMontoSalienteUsd}</div>}
                </Form.Group>

                <Form.Group>
                  <Form.Label className="fw-bold">{properties.cost_ti_reason_label}</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    name="tiMotivoUsoRecurrente"
                    value={data.tiMotivoUsoRecurrente ?? ''}
                    onChange={handleChange}
                    className="rounded-0"
                    maxLength={225}
                  />
                  {modalErrors.tiMotivoUsoRecurrente && <div className="text-danger small mt-1">{modalErrors.tiMotivoUsoRecurrente}</div>}
                </Form.Group>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" className="rounded-0" onClick={() => setShowExtraDataModal(false)}>
            {properties.btn_back}
          </Button>
          <Button variant="primary" className="rounded-0" onClick={handleSaveModal}>
            {properties.btn_continue}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}